import React, { useMemo } from "react";
import { useUserAvatar } from "@/hooks/useUserAvatar";
import {
  PersonAvatar,
  type AvatarSize,
} from "@/components/ui/person-avatar";
import { usePrivate } from "@/contexts/PrivateContext";

interface UserAvatarProps {
  /** Size of the avatar */
  size?: AvatarSize;
  /** Custom className */
  className?: string;
  /** Show fallback with user initials when photo is unavailable */
  showFallback?: boolean;
  /** Custom photo filename (if provided, uses this instead of fetching from profile) */
  photoFilename?: string | null;
  /** Custom user ID (if provided, uses this for default avatar generation) */
  userId?: string;
  /** Custom user name (for fallback initials) */
  userName?: string;
  /** Accessible label — defaults to a name-based label in Portuguese */
  ariaLabel?: string;
  /** When true, the avatar is decorative (parent provides the accessible name) */
  decorative?: boolean;
}

/**
 * UserAvatar — logged-in user's profile photo with resilient loading.
 * Fetches the student profile when no custom photo is supplied.
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
  size = "md",
  className,
  showFallback = true,
  photoFilename: customPhotoFilename,
  userId: customUserId,
  userName: customUserName,
  ariaLabel,
  decorative = false,
}) => {
  const { name, id: currentUserId } = usePrivate();
  const { avatarUrl: hookAvatarUrl, isLoading } = useUserAvatar();

  const usesCustomPhoto = customPhotoFilename !== undefined;
  const userId = customUserId || currentUserId || undefined;
  const userName = customUserName || name || undefined;

  const label =
    ariaLabel ??
    (userName ? `Avatar de ${userName}` : "Seu avatar de perfil");

  return (
    <PersonAvatar
      userId={userId}
      userName={userName}
      photoFilename={usesCustomPhoto ? customPhotoFilename : undefined}
      avatarUrl={usesCustomPhoto ? undefined : hookAvatarUrl}
      size={size}
      className={className}
      showFallback={showFallback}
      isLoading={isLoading}
      ariaLabel={label}
      decorative={decorative}
    />
  );
};
